/**
 * Answer Generation Panel
 *
 * Interface for generating AI answers for questions in bulk
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { authFetch } from '@/utils/auth-fetch';
import { Loader2, Sparkles, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface QuestionWheel {
  id: string;
  name: string;
  description: string;
  wheel_type: string;
  question_count: number;
  language: string;
  status: string;
}

interface GenerationJob {
  id: string;
  wheel_id: string;
  wheel_name: string;
  status: string;
  total_questions: number;
  processed_questions: number;
  successful_answers: number;
  failed_answers: number;
  created_at: string;
  completed_at?: string;
  error?: string;
}

interface AnswerGenerationPanelProps {
  siteId: string;
  sectorId?: number | null; // sites.sector_id is an integer; only interpolated into the URL
}

export function AnswerGenerationPanel({ siteId, sectorId }: AnswerGenerationPanelProps) {
  const [wheels, setWheels] = useState<QuestionWheel[]>([]);
  const [selectedWheel, setSelectedWheel] = useState<string>('');
  const [maxProducts, setMaxProducts] = useState<number>(100);
  const [language, setLanguage] = useState<string>('en');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState<GenerationJob | null>(null);
  const [recentJobs, setRecentJobs] = useState<GenerationJob[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    loadWheels();
    loadRecentJobs();
  }, [sectorId]);

  useEffect(() => {
    // Poll job status if there's an active job
    if (currentJob && currentJob.status === 'in_progress') {
      const interval = setInterval(() => {
        checkJobStatus(currentJob.id);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [currentJob]);

  const loadWheels = async () => {
    if (!sectorId) {
      setWheels([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`/sectors/${sectorId}/question-wheels`);
      const data = await response.json();
      if (data.success) {
        setWheels(data.wheels || []);
      }
    } catch (error) {
      console.error('Failed to load question wheels:', error);
      toast({
        title: 'Error',
        description: 'Failed to load question wheels',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRecentJobs = async () => {
    try {
      const response = await authFetch(`/qa/sites/${siteId}/generation-jobs?limit=5`);
      const data = await response.json();
      if (data.success) {
        setRecentJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to load recent jobs:', error);
    }
  };

  const checkJobStatus = async (jobId: string) => {
    try {
      const response = await authFetch(`/qa/sites/${siteId}/generation-jobs/${jobId}`);
      const data = await response.json();
      if (data.success && data.job) {
        setCurrentJob(data.job);
        if (data.job.status !== 'in_progress') {
          setGenerating(false);
          loadRecentJobs();
          if (data.job.status === 'completed') {
            toast({
              title: 'Generation Complete',
              description: `Successfully generated ${data.job.successful_answers} answers`
            });
          } else if (data.job.status === 'failed') {
            toast({
              title: 'Generation Failed',
              description: data.job.error || 'Unknown error occurred',
              variant: 'destructive'
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to check job status:', error);
    }
  };

  const handleGenerate = async () => {
    if (!selectedWheel) {
      toast({
        title: 'Validation Error',
        description: 'Please select a question wheel',
        variant: 'destructive'
      });
      return;
    }

    setGenerating(true);
    try {
      const response = await authFetch(`/qa/sites/${siteId}/generate-answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wheel_id: selectedWheel,
          language,
          max_products: maxProducts
        })
      });

      const data = await response.json();
      if (data.success && data.job) {
        setCurrentJob(data.job);
        toast({
          title: 'Generation Started',
          description: 'Answer generation job has been queued'
        });
      } else {
        throw new Error(data.error || 'Failed to start generation');
      }
    } catch (error: any) {
      console.error('Failed to generate answers:', error);
      toast({
        title: 'Generation Failed',
        description: error.message,
        variant: 'destructive'
      });
      setGenerating(false);
    }
  };

  const getJobStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'in_progress':
        return <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />In Progress</Badge>;
      case 'failed':
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'queued':
        return <Badge variant="outline"><Info className="h-3 w-3 mr-1" />Queued</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const selectedWheelData = wheels.find(w => w.id === selectedWheel);
  const progress = currentJob
    ? (currentJob.processed_questions / currentJob.total_questions) * 100
    : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generate AI Answers</CardTitle>
          <CardDescription>
            Generate contextual answers using AI and your product catalog
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Configuration Form */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="wheel-select">Question Wheel</Label>
                  <Select value={selectedWheel} onValueChange={setSelectedWheel} disabled={generating}>
                    <SelectTrigger id="wheel-select">
                      <SelectValue placeholder="Select a question wheel" />
                    </SelectTrigger>
                    <SelectContent>
                      {wheels.map((wheel) => (
                        <SelectItem key={wheel.id} value={wheel.id}>
                          {wheel.name} ({wheel.question_count} questions)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedWheelData && (
                    <p className="text-sm text-muted-foreground">
                      {selectedWheelData.description}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="language-select">Language</Label>
                  <Select value={language} onValueChange={setLanguage} disabled={generating}>
                    <SelectTrigger id="language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                      <SelectItem value="it">Italian</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max-products">Max Products to Use</Label>
                  <Input
                    id="max-products"
                    type="number"
                    min={10}
                    max={10000}
                    value={maxProducts}
                    onChange={(e) => setMaxProducts(parseInt(e.target.value) || 100)}
                    disabled={generating}
                  />
                  <p className="text-sm text-muted-foreground">
                    Maximum number of products to analyze for context
                  </p>
                </div>
              </div>

              {/* Generate Button */}
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleGenerate}
                  disabled={generating || !selectedWheel}
                  className="w-full md:w-auto"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Answers
                    </>
                  )}
                </Button>
                {selectedWheelData && (
                  <p className="text-sm text-muted-foreground">
                    Will generate {selectedWheelData.question_count} answers
                  </p>
                )}
              </div>

              {/* Current Job Progress */}
              {currentJob && currentJob.status === 'in_progress' && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Generation Progress</h4>
                    {getJobStatusBadge(currentJob.status)}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{currentJob.wheel_name}</span>
                      <span>{currentJob.processed_questions} / {currentJob.total_questions}</span>
                    </div>
                    <Progress value={progress} />
                    <div className="flex gap-4 text-sm text-muted-foreground">
                      <span>✓ {currentJob.successful_answers} successful</span>
                      {currentJob.failed_answers > 0 && (
                        <span className="text-destructive">✗ {currentJob.failed_answers} failed</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Jobs */}
      {recentJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Generation Jobs</CardTitle>
            <CardDescription>
              View status of recent answer generation runs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentJobs.map((job) => (
                <div key={job.id} className="rounded-lg border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{job.wheel_name}</h4>
                    {getJobStatusBadge(job.status)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Total Questions</p>
                      <p className="font-medium">{job.total_questions}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Successful</p>
                      <p className="font-medium text-green-600">{job.successful_answers}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Failed</p>
                      <p className="font-medium text-destructive">{job.failed_answers}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Started</p>
                      <p className="font-medium">{new Date(job.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {job.error && (
                    <p className="text-sm text-destructive mt-2">{job.error}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
